import type { GUID } from "@faro-lotv/foundation";
import type {
  ExclusiveModeCompletionAction,
  OnlyUiState,
  ToolName,
  ToolVisibility,
} from "./ui-slice";

/**
 * @returns true if the modal spinner must be shown
 */
export function selectShowSpinner({ ui }: OnlyUiState): boolean {
  return ui.showSpinner;
}

/**
 * @returns The name of the active tool
 */
export function selectActiveTool({ ui }: OnlyUiState): ToolName | null {
  return ui.activeTool;
}

/** @returns The active settings menu */
export function selectActiveSettingsMenu({ ui }: OnlyUiState): ToolName | null {
  return ui.activeSettingsMenu;
}

/**
 * @returns The visibility status of all tools
 */
export function selectToolVisibility({
  ui,
}: OnlyUiState): Record<ToolName, ToolVisibility> {
  return ui.toolVisibility;
}

/**
 * @returns Visibility of a specific tool
 * @param toolName Name of the tool
 */
export function selectVisibilityOfTool(toolName: ToolName) {
  return ({ ui }: OnlyUiState): ToolVisibility => ui.toolVisibility[toolName];
}

/**
 * @returns true if the project overview should be open, false if collapsed
 */
export function selectIsProjectOverviewExpanded({ ui }: OnlyUiState): boolean {
  return ui.isProjectOverviewExpanded;
}

/**
 * @returns true if the quick help panel is open, false if collapsed
 */
export function selectIsQuickHelpOpen({ ui }: OnlyUiState): boolean {
  return ui.isQuickHelpOpen;
}

/**
 * @returns true if the unassigned 2D photos panel is visible, false otherwise
 */
export function selectIsUnassigned2dPhotosPanelVisible({
  ui,
}: OnlyUiState): boolean {
  return ui.isUnassigned2dPhotosPanelVisible;
}

/**
 * @returns the current executing exclusive mode completion action
 */
export function selectExclusiveModeCompletionAction({
  ui,
}: OnlyUiState): `${ExclusiveModeCompletionAction}` | undefined {
  return ui.exclusiveModeCompletionAction;
}

/**
 * @param id of the annotation to check
 * @returns true if that annotation is expanded
 */
export function selectIsAnnotationExpanded(id: GUID) {
  return ({ ui }: OnlyUiState): boolean =>
    ui.expandedAnnotationsIds.includes(id);
}

/**
 * @returns The tool to activate when the next mode is active
 */
export function selectNextModeActiveTool({ ui }: OnlyUiState): ToolName | null {
  return ui.nextModeActiveTool;
}
